import {
  Accordion,
  type AllDualVariantIconKeys,
  AnimateHeightBox,
  Body,
  Box,
  Caption,
  Divider,
  FramedIcon,
  Heading,
  Icon,
  MenuItem,
  ProgressBar,
  Stack,
  hFlex,
  useTheme,
  vFlex,
} from "@biom3/react";
import type { Content } from "@prismicio/client";
import { motion } from "motion/react";
import { Fragment, useMemo } from "react";

import { BadgeContent } from "./BadgeContent";
import type { QuestState } from "./types";

import { useIsLessThanMediumScreen } from "@ui-kit/hooks";
import { QuestItem, isQuestCompletedForDay } from "./QuestItem";
import { parseTemplate } from "./utils";

export type QuestListProps = {
  title?: string;
  caption?: string;
  game: Content.GameDocument;
  quests: QuestState[];
  locked?: boolean;
  hideOnCompletion?: boolean;
  progressBadge?: {
    icon?: AllDualVariantIconKeys;
    template?: string;
  };
  totalGemsGranted?: number;
  onAllQuestsCompleted: () => void;
  onQuestClick: (questState: QuestState) => void;
  onQuestLockTimerExpired?: (quest: QuestState) => void;
  nextQuest?: {
    url: string;
    title: string;
    label: string;
    caption: string;
  };
  mainQuests?: boolean;
};

export function QuestList({
  title,
  caption,
  game,
  quests,
  hideOnCompletion,
  locked,
  totalGemsGranted,
  nextQuest,
  onQuestClick,
  onQuestLockTimerExpired,
  onAllQuestsCompleted,
  progressBadge,
  mainQuests,
}: QuestListProps) {
  const { base } = useTheme();
  const isSmallScreenMode = useIsLessThanMediumScreen();
  const nextQuestIndex = useMemo(
    () =>
      quests?.findIndex((quest) =>
        ["in-progress", "pending"].includes(quest.status),
      ),
    [quests],
  );

  const { grantedGems, adjustedTotalGems } = useMemo(() => {
    let grantedGems = 0;
    let adjustedTotalGems = totalGemsGranted || 0;

    for (const quest of quests) {
      grantedGems += quest.increase * (quest.completionCount || 0);
      if (quest.recurring && quest.completionCount !== 0) {
        const adjustedIncrease =
          quest.increase * (quest.completionCount || 0) - quest.increase;
        adjustedTotalGems += !isQuestCompletedForDay(quest)
          ? quest.increase
          : adjustedIncrease;
      }
    }

    return { grantedGems, adjustedTotalGems };
  }, [quests, totalGemsGranted]);

  if (!quests?.length) return null;

  const allQuestsCompleted = quests.every(
    (quest) => quest.completed && !quest.recurring,
  );

  if (hideOnCompletion && allQuestsCompleted) return null;
  const enabled = hideOnCompletion ? !locked && !allQuestsCompleted : !locked;
  const progress = (grantedGems / (adjustedTotalGems || 1)) * 100;

  return (
    <Accordion
      sx={{
        mb: "base.spacing.x4",
        ...(locked
          ? { opacity: 0.7, bg: "base.color.translucent.emphasis.100" }
          : { bg: "base.color.translucent.emphasis.200" }),
        "&:hover, &.expanded:hover": {
          bg: "base.color.translucent.emphasis.100",
        },
        "& > .BaseClickable": {
          gap: "0",
          cursor: "default",
          ...(!locked ? { pr: "base.spacing.x3" } : {}),
        },
        "& .Accordion__clickableArea__ButtCon": { display: "none" },
      }}
      expanded={enabled}
      chevronSide="right"
      testId="QuestList"
    >
      <Accordion.TargetLeftSlot gap="0">
        <Stack direction="column" sx={{ pb: "base.spacing.x3" }} gap="0">
          <Caption size="small">{caption}</Caption>
          <Heading size="xSmall" weight="bold">
            {allQuestsCompleted ? nextQuest?.title || title : title}
          </Heading>
        </Stack>

        <ProgressBar
          value={progress}
          variant="determinate"
          motionProfile="gentle"
          sx={{
            pb: "base.spacing.x2",
            "& .barContainer": {
              bg: "base.color.translucent.standard.500",
            },
            "& .barContainer__bar": {
              bg: "base.color.status.success.bright",
            },
          }}
        >
          <ProgressBar.Label
            text={
              <Body
                size="xxSmall"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "base.color.text.body.secondary",
                }}
              >
                <Icon
                  variant="bold"
                  icon="ImxRewards"
                  sx={{
                    w: "base.icon.size.200",
                    h: "base.icon.size.200",
                    mr: "base.spacing.x1",
                    fill: !locked
                      ? "base.color.status.success.bright"
                      : "currentColor",
                  }}
                />
                <Box
                  sx={{
                    mr: "base.spacing.x1",
                    color: !locked
                      ? "base.color.status.success.bright"
                      : "currentColor",
                  }}
                >
                  {grantedGems}
                </Box>
                / {adjustedTotalGems}
              </Body>
            }
          />
          <ProgressBar.Caption
            sx={{ ...hFlex, gap: "base.spacing.x1", alignItems: "center" }}
          >
            {progressBadge?.icon && progressBadge?.template && (
              <BadgeContent
                badgeTemplate={progressBadge.template}
                badgeIcon={progressBadge.icon}
                increase={adjustedTotalGems}
                iconSx={{
                  w: "base.icon.size.200",
                  h: "base.icon.size.200",
                }}
              />
            )}
          </ProgressBar.Caption>
        </ProgressBar>
      </Accordion.TargetLeftSlot>
      {locked && (
        <Accordion.TargetRightSlot
          sx={{
            position: "absolute",
            top: "50%",
            right: "base.spacing.x3",
            transform: "translateY(-50%)",
          }}
        >
          <FramedIcon
            icon="Lock"
            variant="bold"
            circularFrame
            sx={{
              w: "base.icon.size.400",
              h: "base.icon.size.400",
              fill: "base.color.fixed.white.1000",
            }}
          />
        </Accordion.TargetRightSlot>
      )}
      <Accordion.ExpandedContent
        sx={{ pr: "base.spacing.x3", mb: "base.spacing.x4" }}
        className="QuestList__list"
      >
        <Divider />

        <AnimateHeightBox
          innerContainerSx={{ ...vFlex, gap: "base.spacing.x2" }}
        >
          {allQuestsCompleted && (
            <MenuItem
              emphasized
              size="small"
              sx={{
                opacity: 0,
                mt: "base.spacing.x1",
                bg: "base.color.translucent.emphasis.500",
                "&.MenuItem--emphasized": {
                  bg: "base.color.translucent.emphasis.500",
                },
                "&:hover": {
                  bg: "base.color.translucent.emphasis.500",
                  "&.MenuItem--emphasized": {
                    bg: "base.color.translucent.emphasis.500",
                  },
                },
              }}
              rc={
                <motion.a
                  key="allCompleted"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: base.motion.normal.gentle.jsDuration,
                    ease: base.motion.normal.gentle.jsEase,
                  }}
                  onClick={onAllQuestsCompleted}
                  href={nextQuest?.url || "/"}
                />
              }
            >
              <MenuItem.FramedIcon icon="ImxRewards" />
              <MenuItem.Label>
                {parseTemplate(nextQuest?.label || "", {
                  "<name>": (
                    <Fragment key={`nextQuest-${game.data.name}`}>
                      {game.data.name}
                    </Fragment>
                  ),
                })}
              </MenuItem.Label>
              {!isSmallScreenMode && (
                <MenuItem.Caption>{nextQuest?.caption || ""}</MenuItem.Caption>
              )}
              <MenuItem.IntentIcon
                icon="ChevronForward"
                variant="bold"
                sx={{
                  fill: "base.color.text.body.secondary",
                }}
              />
            </MenuItem>
          )}

          {!allQuestsCompleted &&
            quests.map((quest, index) => (
              <QuestItem
                key={`${quest.key}-${quest.label}-${quest.caption}`}
                index={index}
                quest={quest}
                nextQuestIndex={nextQuestIndex}
                locked={!!locked}
                mainQuest={mainQuests}
                onQuestClick={onQuestClick}
                onQuestLockTimerExpired={onQuestLockTimerExpired}
              />
            ))}
        </AnimateHeightBox>
      </Accordion.ExpandedContent>
    </Accordion>
  );
}
